import { useEffect, useState } from "react";
import axios from "axios";
import Barcode from "react-barcode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BarcodeLabels() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [perRow,  setPerRow]  = useState(4); // 4 per row default

  useEffect(() => {
    const params   = new URLSearchParams(window.location.search);
    const barcodes = params.get("barcodes")?.split(",").filter(Boolean) || [];
    if (!barcodes.length) { setLoading(false); return; }

    Promise.all(
      barcodes.map(b =>
        axios.get(`${API}/inventory/lookup/${b}`)
          .then(r => r.data.found ? r.data.charger : null)
          .catch(() => null)
      )
    ).then(results => setItems(results.filter(Boolean)))
     .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <>
      {/* ===== Print toolbar — hidden when printing ===== */}
      <div className="no-print fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <div>
          <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            Barcode Labels
          </p>
          <p className="text-xs text-slate-400">{items.length} label{items.length !== 1 ? "s" : ""} — ready to print</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Per-row selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Labels per row:</span>
            {[2, 3, 4, 5].map(n => (
              <button key={n}
                onClick={() => setPerRow(n)}
                className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors ${
                  perRow === n ? "bg-green-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}>
                {n}
              </button>
            ))}
          </div>
          <button onClick={() => window.history.back()}
            className="text-sm text-slate-500 hover:text-slate-700 px-3 py-1.5 border border-slate-200 rounded-lg">
            ← Back
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-colors"
            data-testid="labels-print-button">
            🖨️ Print All ({items.length})
          </button>
        </div>
      </div>

      {/* ===== Label sheet ===== */}
      <div style={{ paddingTop: "68px", background: "#f8fafc", minHeight: "100vh" }}>
        <div style={{ maxWidth: "794px", margin: "0 auto", padding: "12px" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: `repeat(${perRow}, 1fr)`,
            gap: "6px",
          }}>
            {items.map((item, i) => (
              <div key={i} style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "4px",
                padding: "8px 6px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                pageBreakInside: "avoid",
                breakInside: "avoid",
              }}>
                {/* ONLY the barcode */}
                <Barcode
                  value={item.barcode}
                  width={perRow <= 2 ? 2 : perRow === 3 ? 1.6 : 1.2}
                  height={perRow <= 2 ? 55 : perRow === 3 ? 45 : 38}
                  fontSize={perRow <= 2 ? 12 : 10}
                  margin={2}
                  background="#ffffff"
                  displayValue={false}
                />
                {/* Serial number below barcode */}
                <p style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: perRow <= 2 ? "12px" : perRow === 3 ? "10px" : "9px",
                  fontWeight: 700,
                  color: "#1e293b",
                  margin: "2px 0 0",
                  letterSpacing: "0.02em",
                  textAlign: "center",
                }}>
                  {item.barcode}
                </p>
              </div>
            ))}
          </div>

          {items.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px", color: "#94a3b8" }}>
              <p>No barcodes to print. Go back and select items from inventory.</p>
            </div>
          )}
        </div>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 6mm; }
          .no-print { display: none !important; }
          body { background: white !important; }
          div[style*="paddingTop"] { padding-top: 0 !important; background: white !important; }
        }
      `}</style>
    </>
  );
}
